import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service'
import { take } from 'rxjs/operators'
import { BackendService } from '../../api/backend/backend.service'
import { AccountOpportunityListItemDto } from '../../api/generated/api.generated'
import { CarrierData, StudyDetailsResponse } from '../../api/model/backend.model'
import { ClientYear } from '../../core/model/client.model'
import { AppState } from '../../core/store'
import { selectClientsByID } from '../../core/store/clients.selectors'
import { ProgramInitiationDialogComponent } from '../components/program-initiation-dialog/program-initiation-dialog.component'
import * as fromProgramInitiation from '../store/program-initation.actions'

/**
 * Decides how to initiate a program for a single opportunity row (new program, new year +
 * program, or new/mapped carrier + year + program), confirms with the user, then dispatches
 * the existing program-initiation actions.
 */
@Injectable()
export class ProgramInitiationFlowService {
  private store = inject<Store<AppState>>(Store)
  private dialog = inject(MatDialog)
  private confirmationDialog = inject(ConfirmationDialogService)
  private backendService = inject(BackendService)
  // Provided by the container component, so this is destroyed with it
  private destroyRef = inject(DestroyRef)

  initiate(opportunity: AccountOpportunityListItemDto): void {
    // The row may be stale; check the backend in case a program was created for this oppid since load
    // Cancelled if the user leaves the page first, so no dialog opens on the next page
    this.backendService
      .getStudyByOpportunityId(opportunity.oppId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response.error && response.data) {
          this.showAlreadyExistsDialog(response.data)
        } else {
          // expecting a 404 Not Found error if program doesn't exist
          this.initiateProgram(opportunity)
        }
      })
  }

  private showAlreadyExistsDialog(studyDetails: StudyDetailsResponse): void {
    this.alert(
      `⚠️ Program '${studyDetails?.study?.name}' already exists for this opportunity under ${studyDetails.carrierName} (${studyDetails.year})`,
    )
  }

  private initiateProgram(opportunity: AccountOpportunityListItemDto): void {
    // TODO: Handle date - either ask user to enter year to use or use today's year
    if (!opportunity.opportunityInceptionDate) {
      this.alert(
        `Opportunity: ${opportunity.opportunityName} does not have an associated Inception Date.`,
      )
      return
    }

    // TODO: If both are missing, how should we handle it?
    if (!opportunity.opportunityClass && !opportunity.opportunitySubClass) {
      this.alert(
        `Opportunity: ${opportunity.opportunityName} does not have an associated Class or Sub-Class.`,
      )
      return
    }

    if (opportunity.carrierId != null) {
      this.carrierAlreadyExists(opportunity, opportunity.carrierId)
    } else {
      this.carrierDoesNotExist(opportunity)
    }
  }

  private carrierDoesNotExist(opportunity: AccountOpportunityListItemDto): void {
    // Carrier does not exist - create new Carrier or map to an existing one, depending on user selection
    const dialogRef = this.dialog.open(ProgramInitiationDialogComponent, {
      data: {
        accountName: opportunity.accountName,
        accountOpportunityName: opportunity.opportunityName,
        carriers$: this.backendService.getCarriers(),
      },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.event === 'map_new') {
        this.createNewCarrierYearProgram(
          opportunity,
          result.newCarrierName,
          null,
          result.carrierLogoFile,
        )
      } else if (result?.event === 'map_existing') {
        this.createNewCarrierYearProgram(
          opportunity,
          result.newCarrierName,
          result.mapToCarrierId,
          null,
        )
      }
    })
  }

  private carrierAlreadyExists(
    opportunity: AccountOpportunityListItemDto,
    carrierId: number,
  ): void {
    this.store
      .select(selectClientsByID)
      .pipe(take(1))
      .subscribe((carriersByID) => {
        const inceptionYear = getInceptionYear(opportunity)
        const carrierYear = carriersByID[carrierId]?.clientYears?.find(
          (year) => year.year === inceptionYear,
        )

        if (carrierYear) {
          this.carrierYearAlreadyExists(opportunity, carrierId, carrierYear)
        } else {
          // Carrier exists but CarrierYear does not: create new CarrierYear, then new Program
          this.confirm(
            `This action will create a new SAGE Year and Program for Salesforce Opportunity: ${opportunity.opportunityName}.`,
            () => this.createNewProgram(opportunity, carrierId),
          )
        }
      })
  }

  private carrierYearAlreadyExists(
    opportunity: AccountOpportunityListItemDto,
    carrierId: number,
    carrierYear: ClientYear,
  ): void {
    this.confirm(
      `This action will create a new SAGE Program for Salesforce Opportunity: ${opportunity.opportunityName}.`,
      () => this.createNewProgram(opportunity, carrierId, parseInt(carrierYear.id, 10)),
    )
  }

  // Dispatch appropriate action to create new Program, or new Year and Program
  private createNewProgram(
    opportunity: AccountOpportunityListItemDto,
    carrierId: number,
    carrierYearId?: number,
  ): void {
    const program = {
      name: opportunity.opportunityName,
      description: `From Opportunity ${opportunity.opportunityName}`,
      opportunity_id: opportunity.oppId,
    }

    if (carrierYearId) {
      this.store.dispatch(
        fromProgramInitiation.createNewProgramFromOpportunity({
          program: { ...program, carrier_year_id: carrierYearId },
        }),
      )
    } else {
      // If it doesn't exist, we need to create a CarrierYear first
      this.store.dispatch(
        fromProgramInitiation.createNewYearProgramFromOpportunity({
          program: {
            ...program,
            inceptionYear: getInceptionYear(opportunity),
            carrier_id: carrierId,
          },
        }),
      )
    }
  }

  // Dispatch action to create new Carrier, Year and Program
  private createNewCarrierYearProgram(
    opportunity: AccountOpportunityListItemDto,
    newCarrierName: string | undefined,
    existingCarrierId: number | null,
    carrierLogoFormData: FormData | null,
  ): void {
    // Use default values for most Carrier properties
    let carrier: Partial<CarrierData>
    if (existingCarrierId) {
      carrier = {
        id: existingCarrierId,
        name: newCarrierName,
        sf_acctid: opportunity.accountId,
      }
    } else {
      carrier = {
        name: newCarrierName,
        eps_volatility: 0.5,
        esp_volatility_sector: -0.016208245,
        intercept: 0.679440077,
        prospective_roe: 0.09,
        roe_sector: 0.139592613,
        executive_summary: undefined,
        eula: 'standard-eula.html',
        tax_rate: 0.21,
        surplus: 0,
        roe_target: 0,
        book_value: 50000000000.0,
        sf_acctid: opportunity.accountId,
      }
    }

    this.store.dispatch(
      fromProgramInitiation.createNewCarrierYearProgramFromOpportunity({
        carrier,
        program: {
          name: opportunity.opportunityName,
          description: `From Opportunity ${opportunity.opportunityName}`,
          opportunity_id: opportunity.oppId,
          inceptionYear: getInceptionYear(opportunity),
        },
        carrierLogoFormData,
      }),
    )
  }

  private alert(message: string): void {
    this.confirmationDialog.open({ message, submitLabel: 'Ok' })
  }

  private confirm(message: string, onConfirm: () => void): void {
    this.confirmationDialog
      .open({ message, submitLabel: 'Confirm' })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          onConfirm()
        }
      })
  }
}

function getInceptionYear(opportunity: AccountOpportunityListItemDto): string {
  return opportunity.opportunityInceptionDate?.substring(0, 4) ?? ''
}
